import { LinkedDevice } from '@/model';
import { defineStore } from 'pinia';
import { Mutex } from 'async-mutex';

import {
  isError,
  getLinkedDevices,
  renameLinkedDevice,
  revokeLinkedDevice,
} from '@/services/backend';

interface State {
  // null means uninitialized
  awaitable: null | Promise<Map<string, LinkedDevice> | undefined>;
  error: Error;
  devices: Map<string, LinkedDevice>;
  _mutex: Mutex;
}

export const useLinkedDevicesStore = () => {
  const innerStore = defineStore('linkedDevices', {
    state: (): State => ({
      devices: new Map(),
      awaitable: null,
      error: new Error(),
      _mutex: new Mutex(),
    }),
    getters: {
      routers(state): LinkedDevice[] {
        // TODO: revert vendor === miri whenever we start to trust the daemon again
        return Array.from(state.devices.values()).filter(d => d.role === 'router' || d.vendor === 'miri');
      },
    },
    actions: {
      nameof(uuid: string) { return this.devices.get(uuid)?.friendlyName ?? null },
      async refresh() {
        // cannot guarantee this remote call executes only once
        if (this._mutex.isLocked()) return;
        return await this._mutex.runExclusive(async () => {
          const resp = await getLinkedDevices();

          if (isError(resp)) {
            this.error = new Error('Error fetching linked devices', { cause: resp });
          } else {
            return this.devices = new Map(resp.map(x => [x.deviceUUID, x]));
          }
        });
      },
      init() { return this.awaitable = this.awaitable ?? this.refresh(); },
      async renameLinkedDevice(uuid: string, nickname: string) {
        const resp = await renameLinkedDevice(uuid, nickname);
        if (isError(resp)) return resp;
        this.devices.get(uuid)!.friendlyName = nickname;
      },
      async revokeLinkedDevice(uuid: string) {
        const resp = await revokeLinkedDevice(uuid);
        if (isError(resp)) return resp;
        this.devices.delete(uuid);
      },
    },
  });

  const store = innerStore();
  store.awaitable === null && void store.init();

  return store;
};
